// frontend/src/components/projects/ProjectMembersGrid.tsx
import React, { useMemo } from "react";
import { Alert, Card, Group, Stack, Text } from "@mantine/core";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-enterprise";

import { useAppSelector } from "../app/hooks";
import { selectProjectNo } from "../globalSelectors";
import { selectHttpProject } from "../projectSelectors";
import type { IProjectMember, IProjectMemberRole } from "@multiconsult-group/multiplacer-core";
import { formatShortDate } from "../globalUtils";

type MemberRow = {
  role: IProjectMemberRole;
  addedAt: string;
  userId: string;
};

function roleLabel(role: IProjectMemberRole): string {
  switch (role) {
    case "OWNER":
      return "Eier";
    case "ADMIN":
      return "Admin";
    case "MEMBER":
      return "Medlem";
    case "VIEWER":
      return "Leser";
    default:
      return role;
  }
}

export const ProjectMembersGrid: React.FC = () => {
  const projectNo = useAppSelector(selectProjectNo);
  const project = useAppSelector(selectHttpProject);

  if (!project) return null;

  const rowData: MemberRow[] = useMemo(() => {
    const members = project.members as IProjectMember[] | undefined;
    if (!Array.isArray(members)) return [];

    return members.map((m) => ({
      role: m.role,
      addedAt: m.addedAt ? formatShortDate(m.addedAt) : "",
      userId: String(m.userId ?? ""),
    }));
  }, [project.members]);

  const columnDefs = useMemo<ColDef<MemberRow>[]>(
    () => [
      {
        field: "role",
        headerName: "Rolle",
        width: 160,
        valueFormatter: (p) => roleLabel(p.value as IProjectMemberRole),
      },
      { field: "addedAt", headerName: "Lagt til", width: 170 },
      { field: "userId", headerName: "UserId", flex: 1, minWidth: 260 },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: false,
      filter: false,
      floatingFilter: false,
    }),
    []
  );

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600}>Medlemmer</Text>
          <Text size="sm" c="dimmed">
            Prosjekt {projectNo}
          </Text>
        </Group>

        <div style={{ width: "100%", height: 220 }}>
          <AgGridReact<MemberRow>
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            suppressCellFocus
            headerHeight={36}
            rowHeight={36}
            domLayout="autoHeight"
          />
        </div>
      </Stack>
    </Card>
  );
};
